import { useEffect, useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { trackUserActivity } from "../redux/actions/dashboardActions";
import { ROUTE_TO_ENTITY_MAP } from "../config/trackingConfig";
import { EntityType } from "../shared/enums/EntityType";

interface ActivityRef {
  entityType: EntityType | null;
  startTime: number | null;
}

const useWorkspaceTracking = (): void => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.auth);
  const { activeTabId } = useAppSelector((state) => state.workspace);

  const activityRef = useRef<ActivityRef>({
    entityType: null,
    startTime: null,
  });

  const sendTrackingData = useCallback(
    (entity: EntityType | null, startTime: number | null): void => {
      if (!entity || !startTime) return;

      const timeSpentSeconds = (Date.now() - startTime) / 1000;

      // Only track if user spent more than 3 seconds on the tab
      if (timeSpentSeconds > 3) {
        dispatch(
          trackUserActivity({
            entityType: entity,
            timeSpentSeconds: Math.round(timeSpentSeconds),
          }),
        );
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!userInfo) return;

    // Send data for the PREVIOUS tab before switching to the new one
    sendTrackingData(
      activityRef.current.entityType,
      activityRef.current.startTime,
    );

    // Set up tracking for the NEW tab
    const newEntity = activeTabId
      ? ROUTE_TO_ENTITY_MAP[activeTabId] || null
      : null;

    activityRef.current = {
      entityType: newEntity,
      startTime: Date.now(),
    };
  }, [activeTabId, userInfo, sendTrackingData]);

  useEffect(() => {
    const handleBeforeUnload = (): void => {
      sendTrackingData(
        activityRef.current.entityType,
        activityRef.current.startTime,
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [sendTrackingData]);
};

export default useWorkspaceTracking;
